// Adjuntos en Supabase Storage (bucket privado "adjuntos") + compresión de imágenes.
// Los archivos pesados (documentos firmados de remisiones) ya no viven como base64
// dentro de localStorage/tablas: se suben al bucket y en el documento solo queda la
// ruta (storagePath). Se abren mediante URL firmada temporal.
// Ruta de cada objeto: <tenant_id>/<carpeta>/<id>-<nombre>, y las políticas del
// bucket validan que la primera carpeta coincida con el tenant del usuario.
import { cloudState, supa } from "./supabaseClient.js";

export const BUCKET_ADJUNTOS = "adjuntos";

export async function subirAdjunto(carpeta, id, file) {
  if (!cloudState.tenantId) throw new Error("Sin sesión activa.");
  const nombreSeguro = String(file.name || "archivo").replace(/[^\w.\-]+/g, "_").slice(-80);
  const path = cloudState.tenantId + "/" + carpeta + "/" + id + "-" + nombreSeguro;
  const { error } = await supa.storage.from(BUCKET_ADJUNTOS).upload(path, file, {
    upsert: true,
    contentType: file.type || "application/octet-stream"
  });
  if (error) throw error;
  return path;
}

export async function urlFirmadaAdjunto(path, segundos = 300) {
  const { data, error } = await supa.storage.from(BUCKET_ADJUNTOS).createSignedUrl(path, segundos);
  if (error) throw error;
  return data.signedUrl;
}

// Best-effort: un adjunto huérfano en el bucket no debe bloquear el flujo del usuario.
export async function eliminarAdjunto(path) {
  if (!path) return;
  const { error } = await supa.storage.from(BUCKET_ADJUNTOS).remove([path]);
  if (error) console.error("⚠️ No se pudo eliminar el adjunto del bucket:", error.message);
}

// Reescala una imagen al lado largo indicado y la devuelve como data-URL JPEG.
// Evita que fotos de cámara (varios MB) revienten la cuota de localStorage o
// infle la fila del producto en Supabase.
export function comprimirImagen(file, maxDim = 800, calidad = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const escala = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * escala));
        const h = Math.max(1, Math.round(img.height * escala));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        // El JPEG no tiene transparencia: un PNG recortado (fondo transparente,
        // que es lo normal en las fotos de catálogo de los proveedores) salía
        // con el fondo NEGRO. Se pinta blanco antes de dibujar encima.
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", calidad));
      };
      img.onerror = () => reject(new Error("No se pudo procesar la imagen"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}

// ─── Fotos de producto en el bucket ─────────────────────────────────────────
// Antes vivían como base64 DENTRO de la ficha. 91 fichas llegaron a pesar 35 MB,
// que no caben en la cuota de localStorage: el navegador dejaba de guardar, la
// pantalla mostraba una fracción del catálogo y el guardado siguiente creía que
// el resto se había borrado. Ahora la ficha guarda solo la ruta (unos 40 bytes).
export const CARPETA_PRODUCTOS = "productos";

function rutaProducto(productoId) {
  if (!cloudState.tenantId) throw new Error("Sin sesión activa.");
  return cloudState.tenantId + "/" + CARPETA_PRODUCTOS + "/" + productoId + ".jpg";
}

// Sube la foto (un data-URL, ya comprimido por comprimirImagen) y devuelve su
// ruta. upsert:true porque la ruta depende del id del producto: al cambiarle la
// foto se reemplaza el objeto y no quedan huérfanos acumulándose.
export async function subirImagenProducto(productoId, dataUrl) {
  if (!productoId) throw new Error("Falta el id del producto.");
  if (!/^data:image\//.test(String(dataUrl || ""))) throw new Error("La imagen no es un data-URL.");
  const path = rutaProducto(productoId);
  const blob = await (await fetch(dataUrl)).blob();
  const { error } = await supa.storage.from(BUCKET_ADJUNTOS)
    .upload(path, blob, { upsert: true, contentType: "image/jpeg" });
  if (error) throw error;
  return path;
}

export async function eliminarImagenProducto(productoId) {
  if (!productoId || !cloudState.tenantId) return;
  await eliminarAdjunto(rutaProducto(productoId));
}

// URLs firmadas en LOTE: una sola petición para todo el catálogo, en vez de una
// por ficha. Devuelve un Map ruta → url; las rutas que fallen quedan fuera.
export async function urlsFirmadasProductos(paths, segundos = 60 * 60 * 24) {
  const limpias = [...new Set((paths || []).filter(Boolean))];
  if (!limpias.length) return new Map();
  const { data, error } = await supa.storage.from(BUCKET_ADJUNTOS)
    .createSignedUrls(limpias, segundos);
  if (error) throw error;
  const m = new Map();
  (data || []).forEach(d => { if (d && d.signedUrl && !d.error) m.set(d.path, d.signedUrl); });
  return m;
}

// Descarga un objeto del bucket y lo devuelve como data-URL. Lo necesita el PDF:
// la cotización se abre en otra ventana y se imprime, y una URL que caduque —o
// una red que falle en ese instante— dejaría la cotización SIN fotos, que es
// justo lo que el cliente recibe. Embebida, el PDF es tan sólido como antes.
export async function dataUrlDeStorage(path) {
  if (!path) return "";
  const { data, error } = await supa.storage.from(BUCKET_ADJUNTOS).download(path);
  if (error) throw error;
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("No se pudo leer la imagen descargada"));
    reader.readAsDataURL(data);
  });
}
